import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Button — variant design adopted from the TailGrids component model (the system
 * our Figma is built on), wired to First Bite semantic tokens:
 *
 *   variant     primary | danger | success | ghost   (intent / color)
 *   appearance  fill | outline                        (solid vs bordered)
 *   size        xs | sm | md | lg                     (height + padding)
 *   iconOnly    boolean                               (square, icon-sized button)
 *
 * All colors resolve to semantic tokens (bg-primary, text-destructive, …) so the
 * button inherits the brand palette and is dark-mode ready.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-fbui-ring disabled:pointer-events-none disabled:opacity-50 [&>svg]:shrink-0',
  {
    variants: {
      // Intent colors are applied via compoundVariants (fill vs outline differ).
      variant: { primary: '', danger: '', success: '', ghost: '' },
      appearance: { fill: '', outline: '' },
      size: {
        xs: 'text-xs [&>svg]:size-4',
        sm: 'text-sm [&>svg]:size-4',
        md: 'text-sm [&>svg]:size-5',
        lg: 'text-base [&>svg]:size-5',
      },
      iconOnly: { true: '', false: '' },
    },
    compoundVariants: [
      // ── fill ────────────────────────────────────────────────────────────
      {
        variant: 'primary',
        appearance: 'fill',
        className:
          'bg-fbui-primary text-fbui-primary-foreground hover:bg-fbui-primary-hover active:bg-fbui-primary-active',
      },
      {
        variant: 'danger',
        appearance: 'fill',
        className:
          'bg-fbui-destructive text-fbui-destructive-foreground hover:bg-fbui-destructive-hover',
      },
      {
        variant: 'success',
        appearance: 'fill',
        className:
          'bg-fbui-success text-fbui-success-foreground hover:bg-fbui-success-hover',
      },
      // ── outline ─────────────────────────────────────────────────────────
      {
        variant: 'primary',
        appearance: 'outline',
        className:
          'border border-fbui-primary bg-fbui-background text-fbui-primary hover:bg-fbui-accent hover:text-fbui-accent-foreground',
      },
      {
        variant: 'danger',
        appearance: 'outline',
        className:
          'border border-fbui-destructive bg-fbui-background text-fbui-destructive hover:bg-fbui-destructive-subtle',
      },
      {
        variant: 'success',
        appearance: 'outline',
        className:
          'border border-fbui-success bg-fbui-background text-fbui-success hover:bg-fbui-success-subtle',
      },
      // ── ghost (appearance-agnostic) ─────────────────────────────────────
      {
        variant: 'ghost',
        className:
          'text-fbui-foreground hover:bg-fbui-accent hover:text-fbui-accent-foreground',
      },
      // ── sizing: text buttons (height + horizontal padding) ──────────────
      { iconOnly: false, size: 'xs', className: 'h-8 px-3' },
      { iconOnly: false, size: 'sm', className: 'h-9 px-3.5' },
      { iconOnly: false, size: 'md', className: 'h-10 px-4' },
      { iconOnly: false, size: 'lg', className: 'h-11 px-5' },
      // ── sizing: icon-only buttons (square) ──────────────────────────────
      { iconOnly: true, size: 'xs', className: 'size-8' },
      { iconOnly: true, size: 'sm', className: 'size-9' },
      { iconOnly: true, size: 'md', className: 'size-10' },
      { iconOnly: true, size: 'lg', className: 'size-11' },
    ],
    defaultVariants: {
      variant: 'primary',
      appearance: 'fill',
      size: 'md',
      iconOnly: false,
    },
  }
)

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element as the button (Radix Slot), e.g. an anchor. */
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      appearance,
      size,
      iconOnly,
      asChild = false,
      type,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button'
    // Default native buttons to type="button" so a Button outside a <form> never
    // triggers an accidental submit. When asChild is set we don't own the element.
    return (
      <Comp
        ref={ref}
        type={asChild ? type : (type ?? 'button')}
        className={cn(
          buttonVariants({ variant, appearance, size, iconOnly, className })
        )}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'
